// BL-241 Gate 6 — shared pure helpers for Communications multi-participant state.
//
// Builds against the live collaboration schema verified for the final Gate 6 review:
// staff_presence is a global per-user online/away/offline signal, active_engagements owns the
// polymorphic subject lock, and engagement_join_requests points at active_engagements.id.

export const ENGAGEMENT_SUBJECT_TYPE = 'intake_session';
export const PRESENCE_ONLINE_MS = 5 * 60 * 1000;
export const ENGAGEMENT_ACTIVE_MS = 10 * 60 * 1000;
export const JOIN_STATUS_PENDING = 'pending';
export const JOIN_STATUS_ACCEPTED = 'accepted';
export const JOIN_STATUS_DECLINED = 'declined';
const AVAILABLE_PRESENCE_STATUSES = new Set(['available', 'online']);

function firstString(...values) {
    for (const value of values) {
        if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
}

function toTimestamp(value) {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
}

function isoStamp(now = new Date()) {
    return (now instanceof Date ? now : new Date(now)).toISOString();
}

function isFresh(stamp, now = Date.now(), windowMs = ENGAGEMENT_ACTIVE_MS) {
    const at = toTimestamp(stamp);
    const basis = now instanceof Date ? now.getTime() : now;
    return at !== null && Number.isFinite(basis) && basis - at <= windowMs;
}

export function staffInitials(nameOrEmail, fallback = 'S') {
    const cleaned = firstString(nameOrEmail);
    if (!cleaned) return fallback;
    const words = cleaned.split(/[\s@._-]+/).filter(Boolean);
    if (words.length === 0) return fallback;
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
}

export function displayStaffName(staffUserId, staffNames = {}) {
    return firstString(staffNames?.[staffUserId]) || 'Staff';
}

export function formatRelativeAge(stamp, now = Date.now()) {
    const at = toTimestamp(stamp);
    const basis = now instanceof Date ? now.getTime() : now;
    if (at === null || !Number.isFinite(basis)) return 'recently';
    const seconds = Math.max(0, Math.round((basis - at) / 1000));
    if (seconds < 45) return 'just now';
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 48) return `${hours}h ago`;
    return `${Math.round(hours / 24)}d ago`;
}

export function normalizeStaffPresenceRow(row, { now = Date.now(), staffNames = {}, currentUserId = null } = {}) {
    if (!row || typeof row !== 'object') return null;
    const staffUserId = firstString(row.user_id);
    if (!staffUserId) return null;
    const status = firstString(row.status).toLowerCase() || 'offline';
    const updatedAt = firstString(row.updated_at);
    const statusLabel = status === 'do_not_disturb'
        ? 'Do Not Disturb'
        : status.split('_').filter(Boolean).map((part) => part[0].toUpperCase() + part.slice(1)).join(' ') || 'Offline';
    return {
        userId: staffUserId,
        staffUserId,
        orgId: firstString(row.org_id) || null,
        status,
        statusLabel,
        updatedAt,
        isOnline: AVAILABLE_PRESENCE_STATUSES.has(status) && isFresh(updatedAt, now, PRESENCE_ONLINE_MS),
        name: displayStaffName(staffUserId, staffNames),
        initials: staffInitials(displayStaffName(staffUserId, staffNames)),
        isCurrentUser: currentUserId === staffUserId,
    };
}

export function mapStaffPresenceByUser(rows = [], options = {}) {
    const mapped = {};
    (Array.isArray(rows) ? rows : []).forEach((row) => {
        const normalized = normalizeStaffPresenceRow(row, options);
        if (normalized) mapped[normalized.userId] = normalized;
    });
    return mapped;
}

export function normalizeEngagementRow(row, { now = Date.now(), staffNames = {}, staffPresenceByUser = {} } = {}) {
    if (!row || typeof row !== 'object') return null;
    const id = firstString(row.id);
    const subjectType = firstString(row.subject_type).toLowerCase();
    const subjectId = firstString(row.subject_id);
    const staffUserId = firstString(row.engaged_by);
    const engagedAt = firstString(row.engaged_at);
    if (!id || !subjectType || !subjectId || !staffUserId) return null;
    if (!isFresh(engagedAt, now, ENGAGEMENT_ACTIVE_MS)) return null;
    const staffPresence = staffPresenceByUser[staffUserId] || null;
    return {
        id,
        engagementId: id,
        subjectType,
        subjectId,
        sessionId: subjectType === ENGAGEMENT_SUBJECT_TYPE ? subjectId : null,
        staffUserId,
        engagedBy: staffUserId,
        name: displayStaffName(staffUserId, staffNames),
        initials: staffInitials(displayStaffName(staffUserId, staffNames)),
        lastEngagedAt: engagedAt,
        engagedAt,
        staffPresence,
    };
}

export function normalizeJoinRequest(row, { engagementById = {}, staffNames = {} } = {}) {
    if (!row || typeof row !== 'object') return null;
    const id = firstString(row.id);
    const engagementId = firstString(row.engagement_id);
    const requesterUserId = firstString(row.requested_by);
    if (!id || !engagementId || !requesterUserId) return null;
    const engagement = engagementById[engagementId] || null;
    const status = firstString(row.status).toLowerCase() || JOIN_STATUS_PENDING;
    return {
        id,
        engagementId,
        sessionId: engagement?.sessionId || null,
        requesterUserId,
        requestedBy: requesterUserId,
        engagedUserId: engagement?.staffUserId || null,
        requesterName: displayStaffName(requesterUserId, staffNames),
        engagedName: displayStaffName(engagement?.staffUserId, staffNames),
        status,
        requestedAt: firstString(row.created_at),
        createdAt: firstString(row.created_at),
        resolvedAt: firstString(row.resolved_at),
    };
}

export function normalizeJoinRequests(rows = [], options = {}) {
    return (Array.isArray(rows) ? rows : [])
        .map((row) => normalizeJoinRequest(row, options))
        .filter(Boolean);
}

export function resolveEngagementState({
    sessionId,
    engagements = [],
    joinRequests = [],
    currentUserId = null,
    staffNames = {},
    staffPresenceByUser = {},
    now = Date.now(),
} = {}) {
    const activeEngagements = (Array.isArray(engagements) ? engagements : [])
        .map((row) => normalizeEngagementRow(row, { now, staffNames, staffPresenceByUser }))
        .filter((row) => row?.subjectType === ENGAGEMENT_SUBJECT_TYPE && row.subjectId === sessionId)
        .sort((a, b) => toTimestamp(b.engagedAt) - toTimestamp(a.engagedAt));
    const primary = activeEngagements[0] || null;
    const engagementById = Object.fromEntries(activeEngagements.map((engagement) => [engagement.id, engagement]));
    const requests = normalizeJoinRequests(joinRequests, { engagementById, staffNames })
        .filter((request) => request.sessionId === sessionId);
    const acceptedRequests = requests.filter((request) => request.status === JOIN_STATUS_ACCEPTED);
    const currentUserAccepted = acceptedRequests.some((request) => request.requesterUserId === currentUserId || request.engagedUserId === currentUserId);
    const isPrimary = Boolean(primary && primary.staffUserId === currentUserId);
    const isLockedByOther = Boolean(primary && !isPrimary && !currentUserAccepted);
    const pendingRequest = primary
        ? requests.find((request) => request.status === JOIN_STATUS_PENDING && request.requesterUserId === currentUserId && request.engagementId === primary.id) || null
        : null;
    const incomingRequests = requests.filter((request) => (
        request.status === JOIN_STATUS_PENDING
        && primary
        && primary.staffUserId === currentUserId
        && request.engagementId === primary.id
    ));

    return {
        primary,
        isPrimary,
        isLockedByOther,
        isJoined: Boolean(currentUserAccepted && !isPrimary),
        canReply: !primary || isPrimary || currentUserAccepted,
        pendingRequest,
        incomingRequests,
        acceptedRequests,
    };
}

export function collectCollaborationStaffUserIds({ presenceRows = [], engagementRows = [], joinRequests = [] } = {}) {
    const ids = new Set();
    (Array.isArray(presenceRows) ? presenceRows : []).forEach((row) => {
        const id = firstString(row.user_id);
        if (id) ids.add(id);
    });
    (Array.isArray(engagementRows) ? engagementRows : []).forEach((row) => {
        const id = firstString(row.engaged_by);
        if (id) ids.add(id);
    });
    (Array.isArray(joinRequests) ? joinRequests : []).forEach((row) => {
        const id = firstString(row.requested_by);
        if (id) ids.add(id);
    });
    return [...ids];
}

export function buildEngagementUpsert({ sessionId, userId, orgId, now = new Date() } = {}) {
    return {
        subject_type: ENGAGEMENT_SUBJECT_TYPE,
        subject_id: sessionId,
        engaged_by: userId,
        org_id: orgId,
        engaged_at: isoStamp(now),
    };
}

export function buildJoinRequestInsert({ engagementId, requesterUserId } = {}) {
    return {
        engagement_id: engagementId,
        requested_by: requesterUserId,
        status: JOIN_STATUS_PENDING,
    };
}

export function buildJoinRequestPatch({ status, now = new Date() } = {}) {
    const normalized = status === JOIN_STATUS_ACCEPTED ? JOIN_STATUS_ACCEPTED : JOIN_STATUS_DECLINED;
    return {
        status: normalized,
        resolved_at: isoStamp(now),
    };
}
